export interface SendAsAccount {
  name?: string
  email: string
  [key: string]: unknown
}

export interface WorkHour {
  enabled: boolean
  start: number
  end: number
}

export interface EditorFont {
  editorFontFamily: string
  editorFontSizeString: string
  editorFontColorReFwd: string
  editorFontColorNew: string
}

export interface ICSSubscriptionSync {
  id: string
  url: string
  displayName: string
  color: string
  refreshInterval: string
  isSync: boolean
}

export interface CalendarSettings {
  // sync 대상 필드
  defaultDuration: number
  defaultReminderTime: number
  defaultReminderTimeForAll: number
  firstdayOfWeek: number
  hideDeclinedMeeting: boolean
  hideCanceledMeeting: boolean
  showWeekNumber: boolean
  firstWeekNumber: number
  calendarSyncTargets: string[] | null
  icsSubscriptions: ICSSubscriptionSync[] | null
  showSecondaryTimezone: boolean
  secondaryTimezone: string | null
  secondaryTimezoneLabel: string | null
  defaultCalendar: string | null
  workHours: WorkHour[]
}

export interface LocalCalendarSettings {
  calendarViewType: string
  taskDrawerOpen: boolean
  lastAddedCalendar: string | null
  primaryTimezone: string | null
  primaryTimezoneLabel: string | null
  autoAcceptSharedCalendarNoti: boolean
}

export interface TodoSortBy {
  [key: string]: string
}

export interface TodoSettings {
  // sync 대상 필드
  todoSyncTargets: string[] | null
  defaultReminderStartTime: number
  defaultReminderTimeForToday: number
  defaultReminderTimeForFuture: string
  defaultDueDate: string
  defaultFolder: string | null
}

export interface LocalTodoSettings {
  filterType: string
  sortBy: TodoSortBy
  sortDirection: Record<string, string>
  collapsedSectionKeys: Record<string, boolean>
  lastAddedFolder: string | null
}

export interface AiSettings {
  summary: boolean
  language: string
}

export interface AlwaysEmailMap {
  [key: string]: string
}

export interface AccountSettings {
  accountName: string | null
  accountColor: string | null
  accountSenderName: string | null
  alwaysCc: AlwaysEmailMap
  alwaysBcc: AlwaysEmailMap
  replyTo: string
  signatureForNew: string
  signatureForReplyForward: string
}

export interface GeneralSettings {
  locale: string
  dateFormat: string
  timeFormat: string
}

export interface EmailSettings {
  mailUseThread: boolean
  visibleThreadItems: string[]
  viewMode: string
  selectionBehavior: string
  editorFont: EditorFont
  recipientsSearchTarget: string[]
  recipientsSearchResultOrder: string[]
  confirmBeforeSend: boolean
  alwaysRequestReadReceipt: boolean
  showOriginalMessage: boolean
  autoAdvanced: number
  isUnknownAlwaysShowExternalResources: number
  isKnownAlwaysShowExternalResources: number
  isMarkAsReadWhenOpen: boolean
  isMarkAsReadWhenDeleted: boolean
  isAutoMarkAsReadWhenReplyingOrForwarding: boolean
}

export interface LocalEmailSettings {
  theme: string
  sendAsAccounts: SendAsAccount[]
  groupMails: SendAsAccount[]
  defaultSendAsAccount: string | SendAsAccount
  emailListPageSize: number
  useEmailSync: boolean
  syncFrequency: number
}

export interface LocalContactsSettings {
  contactOrganizationExpandedKeys: string[] | null
}

export interface LocalSettings {
  email: LocalEmailSettings
  contacts: LocalContactsSettings
  calendar: LocalCalendarSettings
  todo: LocalTodoSettings
}

// ─── Settings version ──────────────────────────────────────
// IndexedDB 마이그레이션 + 서버 스키마 버전 공용
export const settingsVersion = 3

// ─── Migration accessor ────────────────────────────────────
// settingsMigration이 DB와 plain 객체 모두에서 동작하도록 추상화
export interface MigrationAccessor<T> {
  type: string
  getValue: (target: T, key: string) => unknown | Promise<unknown>
  setValue: (target: T, key: string, value: unknown) => unknown | Promise<unknown>
  removeValue: (target: T, key: string) => unknown | Promise<unknown>
}

export interface SettingsState {
  // 버전 (DB 마이그레이션 + 서버 데이터 버전 비교 공용)
  version: number

  // sync 카테고리 (Server path 구조와 동일)
  account: AccountSettings
  general: GeneralSettings
  email: EmailSettings
  calendar: CalendarSettings
  todo: TodoSettings
  ai: AiSettings

  // local-only
  localSettings: LocalSettings

  // ETag (서버 sync용, 서버 전송 시 제외)
  eTag: string | null

  [key: string]: unknown
}

// ─── Valid settings categories ─────────────────────────────
// SettingsState에서 명시적으로 선언된 카테고리 키 목록.
// CHANGE_SETTING saga는 dot-path의 첫 세그먼트(category)가 이 목록에 포함된 경우에만 처리한다.
// 인덱스 시그니처([key: string]: unknown)로 인해 임의의 키가 허용되는 것을 방지한다.
export const VALID_SETTINGS_CATEGORIES = [
  'version',
  'eTag',
  'account',
  'general',
  'email',
  'calendar',
  'todo',
  'ai',
  'localSettings',
] as const

export type SettingsCategory = (typeof VALID_SETTINGS_CATEGORIES)[number]

export const isValidSettingsCategory = (key: string): key is SettingsCategory =>
  (VALID_SETTINGS_CATEGORIES as readonly string[]).includes(key)

export type ChangeSettingPayload = Record<string, unknown>

export interface SetThemePayload {
  theme: string
}

export interface SetLocalePayload {
  locale: string
}

export interface ReloadSettingsPayload {
  name?: string
  email?: string
}

// ─── Local key routing ─────────────────────────────────────
// CHANGE_SETTING saga와 STORED_SETTINGS에서 공유

export const LOCAL_KEY_ROUTING: Record<string, keyof LocalSettings> = {
  // email 그룹 (flat keys)
  theme: 'email',
  sendAsAccounts: 'email',
  groupMails: 'email',
  defaultSendAsAccount: 'email',
  emailListPageSize: 'email',
  useEmailSync: 'email',
  syncFrequency: 'email',
  // contacts 그룹
  contactOrganizationExpandedKeys: 'contacts',
}

// ─── Sync key routing ──────────────────────────────────────
// CHANGE_SETTING saga에서 flat key를 카테고리 그룹으로 라우팅

export const SYNC_KEY_ROUTING: Record<string, keyof SettingsState> = {
  // account
  accountName: 'account',
  accountColor: 'account',
  accountSenderName: 'account',
  alwaysCc: 'account',
  alwaysBcc: 'account',
  replyTo: 'account',
  signatureForNew: 'account',
  signatureForReplyForward: 'account',
  // general
  locale: 'general',
  dateFormat: 'general',
  timeFormat: 'general',
  // email
  mailUseThread: 'email',
  visibleThreadItems: 'email',
  viewMode: 'email',
  selectionBehavior: 'email',
  editorFont: 'email',
  recipientsSearchTarget: 'email',
  recipientsSearchResultOrder: 'email',
  confirmBeforeSend: 'email',
  alwaysRequestReadReceipt: 'email',
  showOriginalMessage: 'email',
  autoAdvanced: 'email',
  isUnknownAlwaysShowExternalResources: 'email',
  isKnownAlwaysShowExternalResources: 'email',
  isMarkAsReadWhenOpen: 'email',
  isMarkAsReadWhenDeleted: 'email',
  isAutoMarkAsReadWhenReplyingOrForwarding: 'email',
}

// ─── initialState ──────────────────────────────────────────
// reducers.ts에서 분리: 순환 참조 방지를 위해 types.ts에 위치
import { CONTACT_TYPE, MAILBOX_TYPE, TODO_FILTER_TYPE } from 'popups/Settings/def'
import { SortType } from 'pages/todo/modules/TodoFilter'

export const initialState: SettingsState = {
  // version은 초기값은 0으로 하고, 로딩되거나, 저장을 하는 경우에 현재 버전으로 저장되어야 함.
  // 기본값으로 현재 버전을 사용하면 저장된 데이터에 버전 정보가 없는 경우, 최신 버전으로 간주하게 되어 마이그레이션이 제대로 동작하지 않을 수 있음.
  version: 0,
  eTag: null,

  // === account (sync) ===
  account: {
    accountName: null,
    accountColor: null,
    accountSenderName: null,
    alwaysCc: {},
    alwaysBcc: {},
    replyTo: '',
    signatureForNew: 'default',
    signatureForReplyForward: 'none',
  },

  // === general (sync) ===
  general: {
    locale: 'ko-KR',
    dateFormat: 'YYYY-MM-DD',
    timeFormat: 'HH:mm',
  },

  // === email (sync) ===
  email: {
    mailUseThread: true,
    visibleThreadItems: [MAILBOX_TYPE.SENT],
    viewMode: 'splitHorizontal', // 'noSplit', 'splitHorizontalReverse'
    selectionBehavior: 'firstMessage', // 'manually'
    editorFont: {
      editorFontFamily: 'Malgun Gothic, Calibri, Arial, sans-serif',
      editorFontSizeString: '11pt',
      editorFontColorReFwd: '#1f497d',
      editorFontColorNew: '#000000',
    },
    recipientsSearchTarget: [
      CONTACT_TYPE.COMPANY,
      CONTACT_TYPE.ORGANIZATION,
      CONTACT_TYPE.CUSTOMER,
      CONTACT_TYPE.PERSONAL,
      CONTACT_TYPE.SUGGESTED,
    ],
    recipientsSearchResultOrder: [
      CONTACT_TYPE.COMPANY,
      CONTACT_TYPE.ORGANIZATION,
      CONTACT_TYPE.CUSTOMER,
      CONTACT_TYPE.PERSONAL,
      CONTACT_TYPE.SUGGESTED,
    ],
    confirmBeforeSend: true,
    alwaysRequestReadReceipt: false,
    showOriginalMessage: false,
    autoAdvanced: 1, // 0 - prev, 1 - next, 2 - return to list
    isUnknownAlwaysShowExternalResources: 0, // 0 - always show, 1 - ask
    isKnownAlwaysShowExternalResources: 0, // 0 - always show, 1 - ask
    isMarkAsReadWhenOpen: true,
    isMarkAsReadWhenDeleted: false,
    isAutoMarkAsReadWhenReplyingOrForwarding: false,
  },

  // === calendar (sync) ===
  calendar: {
    defaultDuration: 30,
    defaultReminderTime: -900,
    defaultReminderTimeForAll: -25200,
    firstdayOfWeek: 7,
    hideDeclinedMeeting: true,
    hideCanceledMeeting: true,
    showWeekNumber: false,
    firstWeekNumber: 0,
    calendarSyncTargets: null,
    icsSubscriptions: null,
    showSecondaryTimezone: false,
    secondaryTimezone: null,
    secondaryTimezoneLabel: null,
    defaultCalendar: null,
    workHours: [
      { /*일*/ enabled: false, start: 540, end: 1080 },
      { /*월*/ enabled: true, start: 540, end: 1080 },
      { /*화*/ enabled: true, start: 540, end: 1080 },
      { /*수*/ enabled: true, start: 540, end: 1080 },
      { /*목*/ enabled: true, start: 540, end: 1080 },
      { /*금*/ enabled: true, start: 540, end: 1080 },
      { /*토*/ enabled: false, start: 540, end: 1080 },
    ],
  },

  // === todo (sync) ===
  todo: {
    todoSyncTargets: null,
    defaultReminderStartTime: 540, // 9AM
    defaultReminderTimeForToday: 0, // Auto, 10, 30, 60, 120, 180
    defaultReminderTimeForFuture: 'dueDate', // dueDate, dayBefore, weekBefore
    defaultDueDate: 'no-date', // today, tomorrow, no-date
    defaultFolder: null,
  },

  // === ai (sync) ===
  ai: {
    summary: false,
    language: 'Korean',
  },

  // === localSettings (local-only) ===
  localSettings: {
    email: {
      theme: 'default', // default, dark
      sendAsAccounts: [],
      groupMails: [],
      defaultSendAsAccount: '',
      emailListPageSize: 15,
      useEmailSync: true,
      syncFrequency: -1,
    },
    contacts: {
      contactOrganizationExpandedKeys: null,
    },
    calendar: {
      calendarViewType: 'week', // month, week, day
      taskDrawerOpen: true,
      lastAddedCalendar: null,
      primaryTimezone: null,
      primaryTimezoneLabel: null,
      autoAcceptSharedCalendarNoti: true,
    },
    todo: {
      filterType: TODO_FILTER_TYPE.all, // today, important, planned, overdue, completed
      sortBy: {
        [TODO_FILTER_TYPE.all]: SortType.Title,
        [TODO_FILTER_TYPE.today]: SortType.Created,
        [TODO_FILTER_TYPE.important]: SortType.DueDate,
        [TODO_FILTER_TYPE.planned]: SortType.Planned,
        [TODO_FILTER_TYPE.overdue]: SortType.Overdue,
        [TODO_FILTER_TYPE.completed]: SortType.Completed,
        [TODO_FILTER_TYPE.focusDate]: SortType.Created,
      },
      sortDirection: {},
      collapsedSectionKeys: {},
      lastAddedFolder: null,
    },
  },
}
